// src/apps/chat/store/chat.store.ts
import { StateCreator } from 'zustand';
import { create } from 'zustand';
import { ApiService } from '@/shared/services/api.service';
import { getRcToken, getRcUserId } from '@/apps/rocketchat/services/rcCredentials';
export interface ChatState {
  chatNotificationCount: number;
  setChatNotificationCount: (count: number) => void;
  selectedWebexChat?: string;
  setSelectedWebexChat: (chatId: string) => void;
  selectedWebexRoomId?: string | null;
  setSelectedWebexRoomId: (roomId: string | null) => void;
}

interface ChatStore {
  rcGetChannelInfo: (name: string) => Promise<void>;
  rcCreateChannel: (name: string) => Promise<void>;
  moveToChannel: (name: string) => Promise<void>;
  getChatUsername: (email: string) => string;
  moveToDirectChat: (userName: string) => Promise<void>;
  selectedWebexChat?: string;
  setSelectedWebexChat: (webexEmail: string) => void;
  selectedWebexRoomId?: string | null;
  setSelectedWebexRoomId: (roomId: string | null) => void;
  deleteLastRoomName: () => void;
  alreadyOpened: boolean;
  setAlreadyOpened: (value: boolean) => void;
  lastPath: string;
  lastRoomName: string;
}
const API_ROOT = import.meta.env.VITE_APP_CHAT_SOURCE_URL || 'http://localhost:5500';
const httpBase = import.meta.env.VITE_APP_CHAT_SOURCE_URL!.replace(/\/+$/, '');
const useChatStore = create<ChatStore>((set, get) => ({
  alreadyOpened: false,
  lastRoomName: '',
  lastPath: '',
  selectedWebexChat: undefined,
  setSelectedWebexChat: (chatId: string) => set({ selectedWebexChat: chatId }),
  selectedWebexRoomId: null,
  setSelectedWebexRoomId: (roomId: string | null) => set({ selectedWebexRoomId: roomId }),
  setAlreadyOpened: (value: boolean) => set({ alreadyOpened: value }),
  deleteLastRoomName: async () => {
    try {
      const { lastRoomName } = get();
      const authToken = getRcToken();
      const userId = getRcUserId();

      if (!lastRoomName || !authToken || !userId) return;

      const headers: any = {
        'X-Auth-Token': authToken,
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      };

      const roomInfo = await ApiService.post<any>('rocketchatauth/convertRoom', {
        name: lastRoomName,
      });

      if (!roomInfo?._id) {
        set({ lastRoomName: '' });
        return;
      }

      await fetch(`${httpBase}/api/v1/groups.leave`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ roomId: roomInfo?._id }),
      });
      await ApiService.post<any>('rocketchatauth/leaveandmaybedelete', {
        name: lastRoomName,
        roomId: roomInfo?._id,
      });

      set({ lastRoomName: '' });
    } catch {
      // Suppress error
      set({ lastRoomName: '' });
    }
  },
  rcGetChannelInfo: async (name: string) => {
    try {
      const authToken = getRcToken();
      const userId = getRcUserId();

      if (!name || !userId || !authToken) return null;

      const headers: any = { 'X-Auth-Token': authToken, 'X-User-Id': userId };
      const encodedName = encodeURIComponent(name);
      // Try private group first, then public channel
      for (const endpoint of ['groups.info', 'channels.info']) {
        const res = await fetch(`${httpBase}/api/v1/${endpoint}?roomName=${encodedName}`, {
          headers,
        });
        if (res.ok) return res.json();
      }
      return null;
    } catch {
      // Suppress error
      return null;
    }
  },

  rcCreateChannel: async (name: string) => {
    try {
      const userId = getRcUserId();
      if (!name || !userId) return null;
      return await ApiService.post<any>('rocketchatauth/createroom', { name, userId });
    } catch {
      // Suppress error
      return null;
    }
  },

  getChatUsername: (email: string) => {
    try {
      const [localPart, domain] = email.split('@');
      const domainName = domain.split('.')[0];
      const username = `${localPart}_${domainName}`;
      return username;
    } catch {
      // Suppress error
      return '';
    }
  },

  moveToDirectChat: async (userName: string) => {
    try {
      const authToken = getRcToken();
      const userId = getRcUserId();

      if (!userName || !userId || !authToken) return null;

      const headers: any = {
        'X-Auth-Token': authToken,
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      };

      // Create/open the DM (idempotent)
      const res = await fetch(`${API_ROOT}api/v1/dm.create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ username: userName }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        return json;
      }

      // Tell MainView to route inside the iframe without changing the URL
      window.postMessage(
        { type: 'rc-go', data: { path: `/direct/${encodeURIComponent(userName)}` } },
        '*'
      );
      await get().deleteLastRoomName();
      set({ lastPath: `/direct/${encodeURIComponent(userName)}` });

      return json;
    } catch {
      // Suppress error
      return null;
    }
  },

  moveToChannel: async (roomName: string) => {
    try {
      if (!roomName) return;

      const sanitizeRoomName = (raw: string) => {
        let safe = raw.trim().toLowerCase();
        safe = safe.replace(/[^0-9a-z._-]/g, '-');
        safe = safe.replace(/-+/g, '-');
        safe = safe.replace(/^[-_.]+|[-_.]+$/g, '');
        return safe || 'room';
      };
      const name = sanitizeRoomName(roomName);

      const { rcGetChannelInfo, rcCreateChannel } = get();
      const info = await rcGetChannelInfo(name).catch(() => null);
      if (!info) {
        await rcCreateChannel(name);
      }

      // Invite the current user to the room now that it's confirmed to exist.
      // Must run AFTER the room exists, otherwise the backend may fail to locate it.
      const userId = getRcUserId();
      if (userId) {
        await ApiService.post<any>('rocketchatauth/inviteuser', { userId, name }).catch(() => null);
      }

      window.postMessage(
        { type: 'rc-go', data: { path: `/group/${encodeURIComponent(name)}` } },
        '*'
      );
      await get().deleteLastRoomName();
      set({ lastPath: `/group/${encodeURIComponent(name)}`, lastRoomName: name });
    } catch {
      // Suppress error
    }
  },
}));

export default useChatStore;

export const createChatSlice: StateCreator<ChatState> = (set) => ({
  chatNotificationCount: 0,
  setChatNotificationCount: (count) => set({ chatNotificationCount: count }),
  selectedWebexChat: undefined,
  setSelectedWebexChat: (chatId: string) => set({ selectedWebexChat: chatId }),
  selectedWebexRoomId: null,
  setSelectedWebexRoomId: (roomId: string | null) => set({ selectedWebexRoomId: roomId }),
});
